/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Container,
} from "@mui/material";
import { Security, Smartphone, Fingerprint } from "@mui/icons-material";
import { User } from "../../types/users";
import { useMFA, PasskeyCredential, TOTPDevice } from "../../hooks/useMFA";

interface MFAVerificationProps {
  user: User;
  onVerificationComplete: () => void;
  onEnrollmentRequested?: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({
  user,
  onVerificationComplete,
  onEnrollmentRequested,
}) => {
  const [verificationMethod, setVerificationMethod] = useState<
    "passkey" | "totp" | null
  >(null);
  const [totpCode, setTotpCode] = useState("");
  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([]);
  const [totpDevices, setTOTPDevices] = useState<TOTPDevice[]>([]);

  const {
    isLoading,
    error,
    clearError,
    loadMFADevices,
    verifyWithPasskey,
    verifyWithTOTP,
  } = useMFA();

  const loadDevices = useCallback(async () => {
    try {
      const devices = await loadMFADevices();
      setPasskeys(devices.passkeys);
      setTOTPDevices(devices.totpDevices);
      if (devices.passkeys.length > 0) {
        setVerificationMethod("passkey");
      } else if (devices.totpDevices.length > 0) {
        setVerificationMethod("totp");
      } else {
        setVerificationMethod(null);
      }
    } catch (error) {
      //
    }
  }, [loadMFADevices]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handlePasskeyVerification = useCallback(async () => {
    try {
      await verifyWithPasskey(passkeys);
      onVerificationComplete();
    } catch (error) {}
  }, [passkeys, verifyWithPasskey, onVerificationComplete]);

  const handleTOTPVerification = useCallback(async () => {
    try {
      await verifyWithTOTP(totpCode);
      onVerificationComplete();
    } catch (error) {}
  }, [totpCode, verifyWithTOTP, onVerificationComplete]);

  useEffect(() => {
    if (verificationMethod === "passkey" && passkeys.length > 0) {
      handlePasskeyVerification();
    }
  }, [verificationMethod, passkeys, handlePasskeyVerification]);

  if (isLoading && !verificationMethod) {
    return (
      <Container maxWidth="sm" sx={{ mt: 20 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (verificationMethod === "passkey") {
    return (
      <Container maxWidth="sm" sx={{ mt: 20 }}>
        <Card elevation={3}>
          <CardContent>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Fingerprint
                  sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h5" gutterBottom>
                  Verify with Passkey
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your device's biometrics or security key to verify your
                  identity
                </Typography>
              </Box>

              {error && error !== "The operation was aborted. " && (
                <Alert severity="error" onClose={clearError}>
                  {error}
                </Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setVerificationMethod(null);
                    clearError();
                  }}
                  disabled={isLoading}
                  fullWidth
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePasskeyVerification}
                  disabled={isLoading}
                  fullWidth
                  startIcon={
                    isLoading ? <CircularProgress size={16} /> : <Fingerprint />
                  }
                >
                  {isLoading ? "Verifying..." : "Verify with Passkey"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (verificationMethod === "totp") {
    return (
      <Container maxWidth="sm" sx={{ mt: 20 }}>
        <Card elevation={3}>
          <CardContent>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Smartphone
                  sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                />
                <Typography variant="h5" gutterBottom>
                  Verify with Authenticator App
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter the 6-digit code from your authenticator app
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Verification Code"
                value={totpCode}
                onChange={(e) => {
                  setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  if (error) clearError();
                }}
                placeholder="123456"
                variant="outlined"
                inputProps={{ maxLength: 6 }}
                autoFocus
              />

              {error && (
                <Alert severity="error" onClose={clearError}>
                  {error}
                </Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setVerificationMethod(null);
                    clearError();
                  }}
                  disabled={isLoading}
                  fullWidth
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleTOTPVerification}
                  disabled={isLoading || totpCode.length !== 6}
                  fullWidth
                  startIcon={
                    isLoading ? <CircularProgress size={16} /> : <Smartphone />
                  }
                >
                  {isLoading ? "Verifying..." : "Verify Code"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 20 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack spacing={3}>
            <Box textAlign="center">
              <Security sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Multi-Factor Authentication Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please verify your identity to continue
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" onClose={clearError}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              {passkeys.length > 0 && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setVerificationMethod("passkey")}
                  startIcon={<Fingerprint />}
                  fullWidth
                >
                  Use Passkey
                </Button>
              )}

              {totpDevices.length > 0 && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setVerificationMethod("totp")}
                  startIcon={<Smartphone />}
                  fullWidth
                >
                  Use Authenticator App
                </Button>
              )}

              {passkeys.length === 0 && totpDevices.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    No MFA methods found. You need to set up multi-factor
                    authentication to continue.
                  </Typography>
                  {onEnrollmentRequested && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={onEnrollmentRequested}
                      sx={{ mt: 1 }}
                    >
                      Set Up MFA
                    </Button>
                  )}
                </Alert>
              )}


              <Button
                variant="text"
                onClick={() => {
                  window.location.href = "/logout";
                }}
                disabled={isLoading}
                fullWidth
              >
                Logout
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default MFAVerification;
